import { Component, OnInit, QueryList, ViewChildren, signal, WritableSignal } from '@angular/core';
import { ConfigInput } from '../../interfaces/interfaces';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { SecurityService } from '../../services/security.service';
import { CommonModule } from '@angular/common';
import { InputComponent } from '../../shared/input/input.component';
import { LoaderService } from '../../services/loader.service';
import { HelpService } from '../../services/help.service';
import { RouteService } from 'app/services/route.service';
import { toast } from 'ngx-sonner';

@Component({
  selector: 'app-balance',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputComponent
],
  templateUrl: './balance.component.html',
  styleUrl: './balance.component.css'
})
export class BalanceComponent implements OnInit{
  @ViewChildren(InputComponent) inputComponents!: QueryList<InputComponent>;

  form: FormGroup;
  incomeExtraActivity: number = 0;
  nextRoute: WritableSignal<string> = signal('');
  isSubmitting = signal(false);
  canContinue = signal(false);
  validationMessage = signal('');
  isLoadingData = signal(true);

  dataInputs: WritableSignal<ConfigInput[]> = signal([
    { label: 'Egresos', subLabel: '', formName: 'outcome', placeholder: '', type: 'number', defaultValue: '', enabled: true, validation: [ Validators.required, Validators.max(999999999)], textError: 'Por favor ingresa un valor válido'},
    { label: 'Activos', subLabel: '', formName: 'assets', placeholder: '', type: 'number', defaultValue: '', enabled: true, validation: [Validators.required, Validators.min(400000), Validators.max(999999999)], textError: 'Por favor ingresa un valor válido'},
    { label: 'Pasivos', subLabel: '', formName: 'liabilities', placeholder: '', type: 'number', defaultValue: '', enabled: true, validation: [ Validators.required, Validators.min(100000), Validators.max(999999999)], textError: 'Por favor ingresa un valor válido'},
  ])


  constructor(
    private _router: Router,
    private _title: Title,
    private _securityService: SecurityService,
    private _loaderService: LoaderService,
    private _helpService: HelpService,
    private _routeService: RouteService
  ){
    this._title.setTitle('Onboarding Digital | Balance');
    this.form = new FormGroup({
      outcome: new FormControl(null, [Validators.required, Validators.max(999999999)]),
      assets: new FormControl(null, [Validators.required, Validators.min(400000), Validators.max(999999999)]),
      liabilities: new FormControl(null, [Validators.required, Validators.min(100000), Validators.max(999999999)]),
    });
    this.nextRoute.set(this._routeService.getNextRoute(this._router.url) ?? '');


  }
  async ngOnInit() {
    const authRoute = localStorage.getItem('route');
    if(!authRoute) {
      this._router.navigateByUrl('');
    }
    const currentUrl = this._router.url;
    if(authRoute && currentUrl !== authRoute) {
      await this._router.navigateByUrl(authRoute);
    } else {
      this.getData();
      this._loaderService.hide();
      this._helpService.show();
    }
  }

  getData(){
    this.isLoadingData.set(true);
    this._securityService.getBalance().subscribe({
        next: (response: any) => {
          this.form.patchValue({
            outcome: response.outcome ?? null,
            assets: response.assets ?? null,
            liabilities: response.liabilities ?? null,
          }, { emitEvent: false });

          this.dataInputs.update((inputs:any) =>
            inputs.map((input:any) => ({
              ...input,
              defaultValue: response[input.formName] ?? input.defaultValue
            }))
          );
          this.incomeExtraActivity = response.incomeExtraActivity ? response.incomeExtraActivity : 0;
          this.isLoadingData.set(false);
          this.updateCanContinue();
        },
        error: (error:any) => {
          console.error("ERROR: ",error);
          this.isLoadingData.set(false);
          toast.error('No pudimos cargar tus datos. Puedes ingresarlos manualmente.');
        }
      });
  }

  onFieldStateChange(): void {
    this.updateCanContinue();
  }

  handleValue(event: {formName:string, value:string | number | null}) {
    const parsed = this.parseFieldValue(event.value);
    const control = this.form.get(event.formName);
    control?.setValue(parsed);
    control?.markAsTouched();
    control?.updateValueAndValidity();
    this.updateCanContinue();

    if (this.validationMessage()) {
      this.validationMessage.set('');
    }
  }

  onSubmit(){
    this.validationMessage.set('');
    this.syncFromInputs();

    if (!this.form.valid) {
      this.validationMessage.set('Revisa que todos los campos tengan valores válidos antes de continuar.');
      this.showInputValidationErrors();
      toast.error('Revisa los valores ingresados antes de continuar.');
      return;
    }

    this.isSubmitting.set(true);

    const payload = {
      outcome: this.toNumber(this.form.get('outcome')?.value),
      assets: this.toNumber(this.form.get('assets')?.value),
      liabilities: this.toNumber(this.form.get('liabilities')?.value),
      incomeExtraActivity: this.incomeExtraActivity,
    };

    this._securityService.setBalance(payload).subscribe({
        next: () => {
          this.isSubmitting.set(false);
          localStorage.setItem('route', this.nextRoute());
          this._router.navigateByUrl(this.nextRoute());
        },
        error: (error:any) => {
          this.isSubmitting.set(false);
          console.error("ERROR: ",error);
          toast.error('No pudimos guardar tu información. Verifica tu conexión e intenta de nuevo.');
        }
    });
  }

  getGains(): string {
    const outcome = parseFloat(this.form.get('outcome')?.value);
    const assets = parseFloat(this.form.get('assets')?.value);
    const liabilities = parseFloat(this.form.get('liabilities')?.value);
  
    if (isNaN(this.incomeExtraActivity) || isNaN(outcome) || isNaN(assets) || isNaN(liabilities)) {
      throw new Error('Todos los campos deben ser números válidos.');
    }

    const gains = (this.incomeExtraActivity - outcome) + (assets - liabilities);
    return gains.toString();
  }

  private updateCanContinue(): void {
    this.syncFromInputs();
    this.canContinue.set(this.areAllFieldsFilledAndValid());
  }

  private areAllFieldsFilledAndValid(): boolean {
    const requiredFields = ['outcome', 'assets', 'liabilities'];

    return requiredFields.every((field) => {
      const control = this.form.get(field);
      const value = control?.value;

      return value !== null && value !== undefined && value !== '' && !!control?.valid;
    });
  }

  private syncFromInputs(): void {
    this.inputComponents?.forEach((input) => {
      const { formName, value } = input.getCurrentValue();
      if (formName) {
        this.form.get(formName)?.setValue(this.parseFieldValue(value), { emitEvent: false });
      }
    });
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private showInputValidationErrors(): void {
    this.inputComponents?.forEach((input) => {
      if (!input.getCurrentValue().valid) {
        input.forceShowValidation();
      }
    });
  }

  private parseFieldValue(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const normalized = typeof value === 'number'
      ? value
      : parseFloat(String(value).replace(/,/g, ''));

    return Number.isNaN(normalized) ? null : normalized;
  }

  private toNumber(value: unknown): number {
    const parsed = this.parseFieldValue(value as string | number | null);
    return parsed ?? 0;
  }

}
